import { forwardRef, useEffect, useState } from 'react'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Search from '@mui/icons-material/Search'
import { skipToken } from '@reduxjs/toolkit/query'

import { ISelectOption } from '$/types'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { useGetQRAScenarioQuery } from 'app/services/api/qra/assessmentScenarios'
import { useGetActiveSystemScopeFilterProfileQuery } from 'app/services/api/filterprofiles'

import { SelectDropdownWrapper } from 'common/components/SelectDropdownWrapper'

import CreateScenarioButton from 'features/RiskManagement/Assessments/Scenarios/CreateScenarioButton'
import EditScenarioButton from 'features/RiskManagement/Assessments/Scenarios/EditScenarioButton'
import ScopeInfo from 'features/RiskManagement/Assessments/Components/ScopeInfo'
import { selectSavedFilter } from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'

import { RiskManagementViewProps } from './types'
import { ViewModeShell } from './ViewModeShell'
import WISHeadings from './WISHeadings'
import { useValidatorHandlers } from './utils/useValidatorHandlers'
import { setSelectedScenarioSync } from './whatIfScenarioSlice'
import { selectScenarioForDropdowns } from './whatIfScenarioSelectors'

// to get the name to show up in React DevTools
// otherwise it will be an anonymous function
const RiskManagementView = (
  {
    wisName,
    title,
    scenarioOptions,
    setWisName,
    setViewMode,
    currentAssessment,
    scenarioNameValidationMessage,
    setIsScenarioNameEmpty,
    isScenarioNameValid,
    setWasScenarioSelected,
    previouslyRanScenario
  }: RiskManagementViewProps,
  ref: React.ForwardedRef<HTMLDivElement>
) => {
  const dispatch = useAppDispatch()

  const { systemScopeFilterProfile } =
    useGetActiveSystemScopeFilterProfileQuery()

  const localFilterProfile = useAppSelector(selectSavedFilter)

  const dropdownScenario = useAppSelector(selectScenarioForDropdowns)

  const [selectedScenario, setSelectedScenario] =
    useState<ISelectOption | null>(
      dropdownScenario.value ? dropdownScenario : null
    )

  const { currentData: scenarioFromRTKQ } = useGetQRAScenarioQuery(
    selectedScenario?.value ? (selectedScenario?.value as string) : skipToken
  )

  useEffect(() => {
    if (previouslyRanScenario && !dropdownScenario.value) {
      setSelectedScenario(previouslyRanScenario)
      setWasScenarioSelected(true)
    }
  }, [previouslyRanScenario, setWasScenarioSelected, dropdownScenario.value])

  useEffect(() => {
    if (scenarioFromRTKQ != null && selectedScenario?.value) {
      dispatch(setSelectedScenarioSync(scenarioFromRTKQ))
    }

    if (selectedScenario != null) {
      setWasScenarioSelected(true)
    } else {
      setWasScenarioSelected(false)
    }
  }, [dispatch, scenarioFromRTKQ, selectedScenario, setWasScenarioSelected])

  const validatorHandlers = useValidatorHandlers(setIsScenarioNameEmpty)

  const onEditScenario = () => {
    setViewMode('edit')
  }

  const onCreateScenario = () => {
    setViewMode('create')
  }

  /**
   * Refresh scenario list items after scenario creation/update
   *
   * REVIEW: Once RTKQ is implemented, just invalidate query?
   * TODO: Wire up create/update scenario logic to RTKQ..
   *
   * NOTE: We are using `scenarioFromStore` because we are checking
   *       for changes made by child edit modal..
   */

  return (
    <div ref={ref}>
      <ViewModeShell>
        <div className='flex'>
          <div className='w-1/2 pl-1'>
            <WISHeadings>{title}</WISHeadings>
            <div className='w-full'>
              <span className='font-semibold'>
                What-If Scenario Name*
              </span>
              <input
                className={`mt-2 w-full rounded-sm ${!isScenarioNameValid ? 'border-red-danger' : ''}`}
                type='text'
                value={wisName}
                onChange={(e) => setWisName(e.target.value)}
                onBlur={validatorHandlers.onFocusOut}
                onFocus={() => setIsScenarioNameEmpty(false)}
              />
              <p className='mt-2 text-sm font-medium text-red-danger'>
                {scenarioNameValidationMessage}
              </p>
              <ScopeInfo
                globalFilterProfile={systemScopeFilterProfile}
                localFilterProfile={localFilterProfile}
                titleClass='pt-8 pb-5 text-gray-700 font-medium'
                gridClass='grid grid-flow-row grid-cols-2'
                fieldClass='font-semibold'
                valueClass=''
                rerunScenarioInfo={currentAssessment}
              />
            </div>
          </div>
          <div className='ml-16 w-1/2 pr-1'>
            <div className='ml-2'>
              <div className='pb-5 pt-10 font-medium uppercase text-gray-700'>
                Conditions
              </div>
              <div className='mt-3 flex before:-ml-2 before:mr-2 before:w-0.5 before:bg-lighthouseBlue'>
                <div className='w-full'>
                  <div className='my-2 font-semibold'>Scenario</div>
                  <div className='mb-2 flex items-center'>
                    <SelectDropdownWrapper
                      showSearch
                      className='w-full'
                      suffixIcon={<Search />}
                      options={scenarioOptions}
                      value={selectedScenario}
                      onChangeSelections={(selections: ISelectOption[]) => {
                        setSelectedScenario(selections?.[0])
                      }}
                      placeholder='Select...'
                    />
                    <div className='ml-2 text-gray-700'>
                      <EditScenarioButton
                        isScenarioSelected={selectedScenario != null}
                        onClick={onEditScenario}
                      />
                    </div>
                  </div>
                  <div>
                    <CreateScenarioButton onClick={onCreateScenario} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ViewModeShell>
      <div className='align-center flex rounded bg-gray-100 px-6 py-4'>
        <InfoOutlinedIcon
          className='mr-2 text-lighthouseBlue'
          data-testid='InfoOutlinedIcon'
        />
        <div className='leading-6'>
          WIS runs can take several hours depending on complexity and may
          restrict your access
        </div>
      </div>
    </div>
  )
}

export const RiskManagementViewWithRef = forwardRef(RiskManagementView)

export default RiskManagementViewWithRef
